// src/pages/Coach/NotHowISayIt.jsx
// STATUS: active | UPDATED: 2026-09-01
// LOG:
//   2026-09-01  New. Catch the correction at the moment it happens.
//
// How Jazz's best feedback has actually reached us so far: he noticed something
// in the product, carried it around, texted Tyler about it hours later, and
// Tyler retyped it for me. Three hops, and only the things annoying enough to
// survive all three ever arrive.
//
// The reaction happened here, in this conversation, next to the exact exchange
// that caused it. That is where it should be catchable.
//
// WHY NOT A FEEDBACK FORM
//
// A form asks him to leave what he was doing, summarise a thing he just read,
// and describe it to somebody who was not there. He will not, and if he does we
// get a paraphrase instead of the exchange.
//
// This is one tap on the message that was wrong. We already have what he said,
// what the coach said back, and which step he was on, so he does not have to
// describe any of it.
//
// AND THEN IT ASKS THE ONLY QUESTION THAT MATTERS
//
// "What should I have said?" A complaint tells us something is broken. His
// answer to that question is a word track, in his words, which is the thing we
// have been trying to get out of his head since he told us the scripts are
// close but not what he uses.
//
// So a moment of irritation becomes the most valuable record in the system, and
// it costs him one tap and one sentence.
//
// LEVEL 00 ONLY, FOR NOW
//
// A salesperson pressing this would mean "that confused me", which is worth
// knowing and is a different thing that deserves different handling. Jazz
// pressing it means the method is wrong, and that is the one we can act on
// today.

import { useState } from 'react'
import {
  Box, HStack, VStack, Text, Textarea, Button, Icon, useToast, Collapse,
} from '@chakra-ui/react'
import { ThumbsDown, Check } from 'lucide-react'
import { supabase } from '../../lib/supabase'

export default function NotHowISayIt({ exchange, stepNumber }) {
  const toast = useToast()
  const [open, setOpen] = useState(false)
  const [text, setText] = useState('')
  const [saving, setSaving] = useState(false)
  const [done, setDone] = useState(false)

  async function save() {
    const said = text.trim()
    setSaving(true)

    // The transcript goes with it. A correction without the exchange that
    // caused it is a complaint; with it, somebody can see exactly what the
    // coach got wrong and check the fix against the same input.
    const content = [
      said ? `HOW JAZZ SAYS IT:\n${said}` : '(No replacement given, just flagged as wrong.)',
      '',
      'THE EXCHANGE:',
      exchange.user ? `He said: ${exchange.user}` : '(no preceding message)',
      `The coach said: ${exchange.assistant}`,
    ].join('\n')

    const { error } = await supabase.from('method_contributions').insert({
      kind: said ? 'word_track' : 'correction',
      step_number: Number.isInteger(stepNumber) ? stepNumber : null,
      title: said
        ? `How he actually says it${stepNumber ? `, Step ${stepNumber}` : ''}`
        : `Coach got this wrong${stepNumber ? `, Step ${stepNumber}` : ''}`,
      content,
    })

    setSaving(false)
    if (error) {
      toast({ status: 'error', title: 'That did not save', description: error.message })
      return
    }
    setDone(true)
    setOpen(false)
    toast({ status: 'success', title: 'Written down, word for word' })
  }

  if (done) {
    return (
      <HStack spacing={1.5} pl="42px" pt={1}>
        <Icon as={Check} boxSize={3} color="accent.500" />
        <Text fontSize="11px" color="inkMuted">
          Written down. It is on the method page.
        </Text>
      </HStack>
    )
  }

  return (
    <Box pl="42px" pt={1}>
      {!open && (
        <HStack
          as="button"
          onClick={() => setOpen(true)}
          spacing={1.5}
          opacity={0.55}
          _hover={{ opacity: 1 }}
          transition="opacity 120ms ease"
        >
          <Icon as={ThumbsDown} boxSize={3} color="inkMuted" />
          <Text fontSize="11px" color="inkMuted" fontWeight={500}>
            That is not how I say it
          </Text>
        </HStack>
      )}

      <Collapse in={open} animateOpacity>
        <VStack align="stretch" spacing={2} mt={2} maxW="520px">
          <Text fontSize="12px" color="inkMuted">
            What should it have said? Say it the way you would say it to a salesperson.
          </Text>
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="In your words"
            rows={3}
            bg="surface"
            borderColor="line"
            fontSize="body-sm"
            _focusVisible={{ borderColor: 'accent.500', boxShadow: 'none' }}
            autoFocus
          />
          <HStack spacing={2}>
            <Button size="sm" bg="ink" color="onInk" _hover={{ opacity: 0.88 }} onClick={save} isLoading={saving}>
              {text.trim() ? 'Write it down' : 'Just flag it'}
            </Button>
            <Button size="sm" variant="ghost" color="inkMuted" onClick={() => setOpen(false)}>
              Cancel
            </Button>
          </HStack>
        </VStack>
      </Collapse>
    </Box>
  )
}
